import type { ConsoleClient } from "../api/client";
import { setStaffPassword } from "../api/console-api";
import { t } from "../i18n";
import { passwordAcceptable } from "./password-rules";

/** The public route, outside the signed-in shell. */
export const SET_PASSWORD_HASH = "#/set-password";

export function isSetPasswordRoute(hash: string): boolean {
  return hash.replace(/^#\/?/, "").split("?")[0] === "set-password";
}

export type SetPasswordForm = { email: string; code: string; password: string; confirm: string };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The first thing to fix before sending, or null when the form can be sent. */
export function setPasswordProblem(form: SetPasswordForm): string | null {
  if (!EMAIL.test(form.email.trim())) return t("setPassword.emailFormat");
  if (!/^[0-9]{6}$/.test(form.code.trim())) return t("setPassword.codeFormat");
  if (!passwordAcceptable(form.email, form.password)) return t("setPassword.rules");
  if (form.password !== form.confirm) return t("setPassword.mismatch");
  return null;
}

export type SetPasswordOutcome =
  | { kind: "done" }
  | { kind: "problem"; message: string }
  | { kind: "failed"; error: unknown };

/**
 * Sends POST /console/set-password once the form passes the same rules as the
 * server. The answer is reported, never assumed: `done` only on success.
 */
export async function submitSetPassword(client: ConsoleClient, form: SetPasswordForm): Promise<SetPasswordOutcome> {
  const problem = setPasswordProblem(form);
  if (problem) return { kind: "problem", message: problem };
  try {
    await setStaffPassword(client, {
      email: form.email.trim().toLowerCase(),
      code: form.code.trim(),
      newPassword: form.password,
    });
    return { kind: "done" };
  } catch (error) {
    return { kind: "failed", error };
  }
}
